import { useCallback, useRef, Dispatch, SetStateAction, CSSProperties } from 'react';
// Fix: Use named imports for ReactFlow functions and types from reactflow.
import { addEdge, Connection, Edge, OnConnectStartParams } from 'reactflow';
import { AppNode } from '../types';
import { DEFAULT_EDGE_STROKE } from '../utils/layoutConstants';

export const useEdgeOperations = (
    nodes: AppNode[],
    setNodes: Dispatch<SetStateAction<AppNode[]>>,
    setEdges: Dispatch<SetStateAction<Edge[]>>
) => {
    const connectingNodeId = useRef<string | null>(null);
    const connectingHandleId = useRef<string | null>(null);

    // Resolve edge label from gateway outputs when sourceHandle maps to an output index
    const resolveGatewayLabel = useCallback((sourceId: string | null, sourceHandle: string | null): string => {
        if (!sourceId || !sourceHandle) return '';
        const sourceNode = nodes.find(n => n.id === sourceId);
        if (!sourceNode) return '';
        const outputs = sourceNode.data?.outputs;
        if (!Array.isArray(outputs)) return '';
        const handleIdx = parseInt(sourceHandle, 10);
        if (isNaN(handleIdx) || handleIdx < 0 || handleIdx >= outputs.length) return '';
        return outputs[handleIdx] || '';
    }, [nodes]);

    const onConnect = useCallback(
        (params: Connection) => setEdges((eds) => {
            const label = resolveGatewayLabel(params.source, params.sourceHandle);
            // Boundary associations should be solid and non-animated; everything else animates.
            const animated = params.sourceHandle !== 'boundary';
            const style: CSSProperties = { stroke: DEFAULT_EDGE_STROKE };

            return addEdge({
                ...params,
                type: 'deletable',
                animated,
                label,
                style,
            }, eds);
        }),
        [setEdges, resolveGatewayLabel]
    );

    const onConnectStart = useCallback((_: any, { nodeId, handleId }: OnConnectStartParams) => {
        connectingNodeId.current = nodeId;
        connectingHandleId.current = handleId;

        setNodes((nds) => nds.map((node) => {
            if (node.id === nodeId) return node;
            return {
                ...node,
                data: {
                    ...node.data,
                    isConnectableTarget: true
                }
            };
        }));
    }, [setNodes]);

    const onConnectEnd = useCallback((event: any) => {
        const target = event.target as HTMLElement;
        const targetNodeElement = target.closest('.react-flow__node') as HTMLElement;
        const isHandle = target.classList.contains('react-flow__handle');

        if (targetNodeElement && !isHandle && connectingNodeId.current) {
            const targetNodeId = targetNodeElement.getAttribute('data-id');

            if (targetNodeId && targetNodeId !== connectingNodeId.current) {
                const label = resolveGatewayLabel(connectingNodeId.current, connectingHandleId.current);
                const animated = connectingHandleId.current !== 'boundary';

                const newEdge = {
                    id: `e-${connectingNodeId.current}-${targetNodeId}-${Date.now()}`,
                    source: connectingNodeId.current,
                    target: targetNodeId,
                    sourceHandle: connectingHandleId.current,
                    type: 'deletable' as const,
                    animated,
                    label,
                    style: { stroke: DEFAULT_EDGE_STROKE },
                };
                setEdges((eds) => addEdge(newEdge, eds));
            }
        }

        setNodes((nds) => nds.map((n) => {
            if (!n.data.isConnectableTarget) return n;
            const { isConnectableTarget, ...rest } = n.data;
            return { ...n, data: rest };
        }));

        connectingNodeId.current = null;
        connectingHandleId.current = null;
    }, [setNodes, setEdges, resolveGatewayLabel]);

    return {
        onConnect,
        onConnectStart,
        onConnectEnd
    };
};